// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import {
  convertBetweenMandFt,
} from 'helpers/unitConversion'

// TODO - break this thing out into multiple files

// this is used to distinguish between null/undefined
// and other falsy values, eg. 0, '', false
// and since redux-form prefers values as strings
// if the value is null / undefined, an empty string is returned.
// if any other value, it is returned cast as a string.
// so '0', '', 'false', or any truthy value
export const castToString = v => v !== null && v !== undefined ? `${v}`.trim() : ''

// o will either be an object or null. if it's an object, get the value for the defined key.
// since there so many lookups that can be null, in place of repeating this logic
// when preparing API data for redux-form, just use this instead!
export const ifObjectGetValue = (o, k) => o ? o[k] : ''

// same as above but specifically returns the id of the entity
// NOTE: this is intended to be used to simple, small entities. (pretty much only lookup tables)
// if your entity has more than one top level property which ends in 'Id'
// you cannot guarantee that this function will handle properly.
// so it will throw an error if it finds more than one property that ends in 'Id'.
// Since redux-form wants all values to be string, this is particularly
// helpful for lookup values which have select or radio groups.
// This is not however as useful when the id does not correspond to
// a form element which is managed by redux-form. (for example companyId on path Details segment ends)
export const ifEntityGetId = o => {
  // if it's falsey, just return an empty string.
  if (!o) {
    return ''
  }

  // get all the keys that end in 'Id'
  const idKeys = Object.keys(o).filter(k => k.slice(-2) === 'Id')

  if (idKeys.length === 0) {
    throw new Error('Provided entity does not have an id.')
  // if there's more than one, throw an exception
  } else if (idKeys.length > 1) {
    throw new Error('Provided entity has more than one id.')
  // if there's exactly one, then return that value
  } else if (idKeys.length === 1) {
    return castToString(o[idKeys[0]])
  }
}

// if value is an empty string or is NaN it returns null
// otherwise it returns the value cast as a number.
// this is useful because redux-form prefers to work with string values
// and the value selected may be '' which should be null for API.
// see how <SelectDynamic /> uses prop emptyOptionLabel for an example of this.
// NOTE - these checks (in place of regex) allow us to support scientific notation in the value
// NOTE - first isNaN check handles false, '', null and undefined
// NOTE - second one handles strings like '1.3e' which parseFloat returns as 1.3
export const castToFloat = v => !isNaN(parseFloat(v)) && !isNaN(+v) ? +v : null

// for numerical values which cannot be 0.
export const castToTruthyInt = v => castToFloat(v) || null

/**
 * takes a string and casts it to bool. returns null if the string value is not 'true' or 'false'
 * @param {String} value
 * @returns {boolean|null}
 */
export const castToBool = value => (
  value === 'false' || value === 'true'
    ? value === 'true'
    : null
)

export const castToYesNo = v => v ? 'Yes' : 'No'

// this is used for multiple radio input groups whose values in API can be true or false. (boolean)
export const yesNoOptions = [{
  label: 'Yes',
  value: 'true',
}, {
  label: 'No',
  value: 'false',
}]

// this is used for multiple radio input groups whose values in API can be true, false or null. (Boolean)
export const yesNoNullOptions = [{
  label: 'Yes',
  value: 'true',
}, {
  label: 'No',
  value: 'false',
}, {
  label: 'N/A',
  value: '',
}]

// returns true if the value's in the array, false if not
export const valueIsInArray = (v, arr) => !!~arr.indexOf(v)

// we have a value in meters,
// take the unitType defined,
// if it's SI, don't convert to anything
// if it's US, convert to ft
// then return the value as a string
export const fromMetersToUnitType = (v, unitType) => (
  castToString(unitType === 'SI' ? v : convertBetweenMandFt(v, unitType))
)

// v is the value to be rounded.
// p is the decimal precision that it will be rounded to
// NOTE - p is concatenated so it be comes scientific notation
export const roundToFixed = (v, p) => (
  castToFloat(v) !== null
    ? Math.round(v * +`1e${p}`) / +`1e${p}`
    : null
)

// rounds the number then returns a string. This way if rounding failed,
// we will only display an empty string and not NaN or null.
export const roundAndCastToString = (v, p) => (
  castToString(roundToFixed(v, p))
)

// NOTE - this is pretty much the same thing as roundAndCastToString
// HOWEVER, it persists trailing zeroes if there are any
// from the specified degree of precision.
export const roundAndLeaveTrailingZeroes = (v, p) => (
  castToFloat(v)?.toFixed(p) || ''
)

// formProps - the third argument passed to the redux-form submit handler.
export const preventFormSubmission = formProps => {
  const {
    asyncBlurFields = [],
    asyncValidating,
  } = formProps
  // a generic means of checking if this redux-form is async validating.
  // this way the submit button doesn't need to be connected to check this
  const isAsyncValidating = !!asyncValidating

  // if the user presses 'enter' when focused on an async blur input field,
  // the form's submit handler will trigger before the input's blur handler can.
  // as a consequence, a user can submit a value without proper validation.
  // if the user is focused on an input field which is assigned an
  // async blur validator, this prevents the form from submitting.

  // get the active element's name attribute. if it's null, save an empty string
  const activeElementName = document.activeElement?.getAttribute('name') || ''

  // when defining an async blur field name from within a fieldArray,
  // redux-form has a specific syntax.
  // eg. input[name] === foo[1]bar'
  // so when defined as an async blur field, it becomes:
  // 'foo[]bar'
  const cleanedName = activeElementName.replace(/\[[^\]]*\]/g, '[]')

  // check to see if the cleaned name is defined in redux-form's asyncBlurFields
  const focusedOnAsyncBlurField = !!~asyncBlurFields.indexOf(cleanedName)

  // force blur on whatever has focus right now.
  // this ensures any autocomplete suggestions will not continue to be displayed
  // AND it will trigger async blur validation if the input field has one assigned.
  // NOTE - if document.activeElement.blur() has already been called in IE11,
  // document.activeElement actually becomes null.
  // as a result, we need null check here
  // adding the document.activeElement.blur check to just be safe.
  if (document.activeElement !== null && document.activeElement.blur) {
    document.activeElement.blur()
  }

  // finally, tell the submit handler if it should actually submit or not.
  return isAsyncValidating || focusedOnAsyncBlurField
}

export formatErrorData from './formatErrorData'
export getNestedByString from './getNestedByString'
export prepareNestedString from './prepareNestedString'
export setNestedValueInObject from './setNestedValueInObject'
export throwSubmissionError from './throwSubmissionError'
